import { Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import MenuBar from "../../menuBar";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../../../hooks/useResponsive";
import { CustomTable } from "../../../components/CustomTable";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getInvoiceDetails,
  getPaymentDetails,
  getPaymentDocumentUrl,
} from "../../../redux/paymentInvoice/action";
import moment from "moment";
import FullPageModal from "../../../components/FullPageLoader";
import storePersist, { localConstant } from "../../../services/localStorage";
import CustomAppBar from "../../../components/CustomAppBar";
import { getPaymentType } from "../../payments/makePayment/utils";
import { ReactComponent as PrintIcon } from "../../../assets/svg/printIcon.svg";
import {
  isMobileDeviceCheck,
  sendMessageToReactNative,
  truncateLocationName,
} from "../../../utils";
import { roundToTwoDecimalPlace } from "../utils";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import SnackbarConfig from "../../../utils/snackbarConfig";

export const InvoiceDetails = () => {
  const myRef = useRef({});
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lgUp = useResponsive("up", "lg");
  const location = useLocation();
  const navigate = useNavigate();
  const { billDetails } = location.state;
  const [loader, setLoader] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [paymentDetails, setpaymentDetails] = useState([]);
  const custId = storePersist.get(localConstant.ENTITY_CUSTOMER_ID);
  const [addressDetails, setAddressDetails] = useState({});
  const updateLoadingStatus = (loadingStatus) => {
    setLoader(loadingStatus);
  };
  useEffect(() => {
    if (billDetails?.isInvoice) {
      updateLoadingStatus(true);
      dispatch(
        getInvoiceDetails(billDetails?.billNumber, custId, (response) => {
          const {
            chargesResponse,
            billHistoryResponse,
            customerAddressResponse,
            allYardsResponse,
          } = response;
          const currentYard = allYardsResponse.filter(
            (item) => item.location_id === billDetails.locationId,
          );
          setInvoiceDetails({ chargesResponse, billHistoryResponse });
          setAddressDetails({
            customerAddressResponse,
            yardsDetails: currentYard[0],
          });
          updateLoadingStatus(false);
        }),
      );
    } else {
      updateLoadingStatus(true);
      dispatch(
        getPaymentDetails(
          billDetails?.paymentNumber,
          {
            paymentVersion: billDetails.paymentVersion,
            paymentCode: billDetails.paymentCode,
          },
          (response) => {
            setpaymentDetails(response);
            updateLoadingStatus(false);
          },
        ),
      );
    }
  }, []);

  const getFormatedDate = (dateToFormat) => {
    return moment(dateToFormat).format("MMMM D, YYYY");
  };

  const getFormatedTime = (dateToFormat) => {
    return moment(dateToFormat).format(" h:mm:ss a");
  };

  const getInvoiceChargesRowData = useMemo(() => {
    if (Object.keys(invoiceDetails).includes("chargesResponse")) {
      const { chargesResponse } = invoiceDetails;
      let rowData = [];
      let totalCharge = 0;
      chargesResponse.forEach((element) => {
        const rowValues = [];
        rowValues.push(element.chrgTypeDesc);
        rowValues.push(getFormatedDate(element.chrgDt));
        rowValues.push(`$${roundToTwoDecimalPlace(element.chrgAmt)}`);
        rowValues.push(element.chrgAmt < 0 ? "Refunded" : "");
        totalCharge += element.chrgAmt;
        rowData.push([...rowValues]);
      });
      return { rowData, totalCharge: roundToTwoDecimalPlace(totalCharge) };
    } else {
      return { rowData: [], totalCharge: 0 };
    }
  }, [invoiceDetails?.chargesResponse]);

  const getInvoiceBillingRowData = useMemo(() => {
    let totalCharge = roundToTwoDecimalPlace(billDetails?.dueAmount);
    if (Object.keys(invoiceDetails).includes("billHistoryResponse")) {
      const { billHistoryResponse } = invoiceDetails;
      let rowData = [];
      billHistoryResponse.forEach((element) => {
        const rowValues = [];
        rowValues.push(element.txnRefNum);
        rowValues.push(getPaymentType(element.paymentModeCd));
        rowValues.push(getFormatedDate(element.paymentDate));
        rowValues.push(`$${roundToTwoDecimalPlace(element.paymentAmount)}`);
        rowData.push([...rowValues]);
      });
      return { rowData, totalCharge };
    } else {
      return { rowData: [], totalCharge };
    }
  }, [invoiceDetails?.billHistoryResponse]);

  const getPaymentRowData = useMemo(() => {
    if (paymentDetails.length > 0) {
      let rowData = [];
      paymentDetails.forEach((element) => {
        const rowValues = [];
        rowValues.push(element.pymtRefTypeNum);
        rowValues.push(getPaymentType(element.pymtRefType));
        rowValues.push(getFormatedDate(element.appliedDt));
        rowValues.push(`$${roundToTwoDecimalPlace(element.pymtAmt)}`);
        rowData.push([...rowValues]);
      });
      return rowData;
    } else {
      return [];
    }
  }, [paymentDetails]);

  const onPressPayNow = () => {
    navigate("/billing_payments/payment", {
      state: {
        billing: {
          charges: [
            {
              totalAmount: billDetails?.dueAmount,
              billNums: billDetails?.billNumber,
            },
          ],
        },
      },
    });
  };

  const convertToPdf = async () => {
    updateLoadingStatus(true);
    const billNumber = billDetails?.isInvoice
      ? billDetails?.billNumber
      : billDetails?.paymentNumber;
    const params = billDetails?.isInvoice
      ? {}
      : {
        paymentVersion: billDetails.paymentVersion,
        paymentTypeCd: billDetails.paymentCode,
      };
    const pdfUrl = await dispatch(
      getPaymentDocumentUrl(billNumber, { ...params }, billDetails?.isInvoice),
    );

    try {
      if (isMobileDeviceCheck()) {
        if (pdfUrl) {
          const pdfConfig = {
            name: `${billDetails?.billNumber}_PDF.pdf`,
            url: pdfUrl?.url,
          };
          sendMessageToReactNative(
            `PDF_DOWNLOAD_NATIVE_EVENT:${JSON.stringify(pdfConfig)}`,
          );
        } else {
          throw new Error('"Error downloading payment PDF."');
        }
      } else {
        const newWindow = window.open(
          pdfUrl.url,
          "_blank",
          "noopener,noreferrer",
        );
        if (newWindow) newWindow.opener = null;
        updateLoadingStatus(false);
      }
    } catch (error) {
      enqueueSnackbar("Error downloading payment PDF.", {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
    } finally {
      updateLoadingStatus(false);
    }
  };
  return (
    <div
      style={{
        width: lgUp ? `calc(100% - ${240}px)` : "100%",
        marginLeft: lgUp ? "240px" : 0,
        position: "relative",
      }}
    >
      <Grid>
        <CustomAppBar
          title={
            billDetails.isInvoice
              ? t("pages.payments.text34")
              : t("pages.payments.text35")
          }
          showBackButton={true}
          onBackButtonClick={() => {
            navigate(-1);
          }}
        />
        <MenuBar myRef={myRef} activeTab={6} />
        <Grid
          width={"100%"}
          display={"flex"}
          justifyContent={"flex-end"}
          style={{ marginTop: "30px", paddingRight: "10px" }}
          onClick={() => {
            convertToPdf();
          }}
        >
          <PrintIcon height={"30px"} width={"30px"} />
        </Grid>
        <Grid
          width={{ sx: "92%", sm: "95%", md: "97%" }}
          display={"flex"}
          flexDirection={"column"}
          margin={2}
          style={{
            border: "1px solid rgb(212, 212, 212)",
            borderRadius: "8px",
          }}
          p={1}
          pt={5}
          id="Invoice-Detials-Container"
        >
          <Grid
            display={"flex"}
            width={{ sx: "92%", sm: "95%", md: "97%" }}
            minHeight={"25vh"}
          >
            {billDetails?.isInvoice ? (
              <Grid
                flex={1}
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"space-between"}
                pl={2}
              >
                {addressDetails?.yardsDetails && (
                  <Grid flex={1}>
                    <Typography color={"black"} fontWeight={"bold"}>
                      Safe Truck Parking
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {truncateLocationName(
                        addressDetails?.yardsDetails?.location_display_name,
                      )}
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {addressDetails?.yardsDetails?.location_address_line_1}
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {addressDetails?.yardsDetails?.location_city}
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {" "}
                      {addressDetails?.yardsDetails?.location_country} -{" "}
                      {addressDetails?.yardsDetails?.location_postal_code}
                    </Typography>
                  </Grid>
                )}
                {addressDetails?.customerAddressResponse && (
                  <Grid flex={1}>
                    <Typography color={"#5E6470"} fontSize={12} mt={1}>
                      Billed to
                    </Typography>
                    <Typography
                      color={"black"}
                      fontWeight={"bold"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {addressDetails?.customerAddressResponse.first_nm}{" "}
                      {addressDetails?.customerAddressResponse.last_nm}
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {addressDetails?.customerAddressResponse.addr_ln_1}
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {addressDetails?.customerAddressResponse.city},{" "}
                      {addressDetails?.customerAddressResponse.cntry_cd} -{" "}
                      {addressDetails?.customerAddressResponse.postal_cd}
                    </Typography>
                    <Typography
                      color={"#5E6470"}
                      fontSize={{ xs: "12px", md: "16px" }}
                    >
                      {addressDetails?.customerAddressResponse.ph_num}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Grid flex={1} display={"flex"} flexDirection={"column"} pl={2}>
                <Grid flex={1}>
                  <Typography color={"#5E6470"} fontSize={12}>
                    {t("pages.payments.text31")}
                  </Typography>
                  <Typography color={"black"} fontWeight={"bold"}>
                    {billDetails.paymentNumber}
                  </Typography>
                </Grid>
                <Grid flex={1}>
                  <Typography color={"#5E6470"} fontSize={12}>
                    {t("pages.payments.text30")}
                  </Typography>
                  <Typography color={"black"} fontWeight={"bold"}>
                    {`$${roundToTwoDecimalPlace(billDetails?.amount)}`}
                  </Typography>
                </Grid>
                <Grid flex={1}>
                  <Typography color={"#5E6470"} fontSize={12}>
                    {t("pages.payments.text29")}
                  </Typography>
                  <Typography color={"black"} fontWeight={"bold"}>
                    {`${getFormatedDate(billDetails?.paymentDate)}`}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid flex={1} display={"flex"} flexDirection={"column"} pl={5}>
              <Grid flex={1}>
                <Typography color={"#5E6470"} fontSize={12}>
                  {billDetails?.isInvoice
                    ? t("pages.payments.text27")
                    : t("pages.payments.text28")}
                </Typography>
                <Typography color={"black"} fontWeight={"bold"}>
                  {billDetails?.isInvoice
                    ? billDetails?.billNumber
                    : billDetails?.transactionNumber}
                </Typography>
              </Grid>
              <Grid flex={1}>
                <Typography color={"#5E6470"} fontSize={12}>
                  {billDetails?.isInvoice
                    ? t("pages.payments.text25")
                    : t("pages.payments.text26")}
                </Typography>
                <Typography color={"black"} fontWeight={"bold"}>
                  {billDetails?.isInvoice
                    ? billDetails?.reservationId
                    : getPaymentType(billDetails?.paymentType)}
                </Typography>
              </Grid>
              <Grid flex={1}>
                <Typography color={"#5E6470"} fontSize={12}>
                  {billDetails?.isInvoice
                    ? t("pages.payments.text23")
                    : t("pages.payments.text24")}
                </Typography>
                <Typography color={"black"} fontWeight={"bold"}>
                  {billDetails?.isInvoice
                    ? getFormatedDate(billDetails?.dueDate)
                    : getFormatedTime(billDetails?.paymentDate)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid mt={5}>
            {billDetails?.isInvoice ? (
              <Grid>
                <Typography color={"black"} fontWeight={"bold"}>
                  Invoice Breakdown
                </Typography>
                <CustomTable
                  tableHeader={[
                    t("pages.payments.text17"),
                    t("pages.payments.text13"),
                    t("pages.payments.text18"),
                    t("pages.payments.text39"),
                  ]}
                  rows={getInvoiceChargesRowData.rowData}
                  isTotalRow={true}
                  totalValue={[
                    t("pages.payments.text22"),
                    "",
                    `$${getInvoiceChargesRowData.totalCharge}`,
                    "",
                  ]}
                />

                <Typography color={"black"} fontWeight={"bold"} mt={5}>
                  Payment History
                </Typography>
                <Grid>
                  <CustomTable
                    tableHeader={[
                      t("pages.payments.text15"),
                      t("pages.payments.text16"),
                      t("pages.payments.text13"),
                      t("pages.payments.text14"),
                    ]}
                    rows={getInvoiceBillingRowData.rowData}
                    isTotalRow={true}
                    totalValue={[
                      t("pages.payments.text21"),
                      "",
                      "",
                      `$${getInvoiceBillingRowData.totalCharge}`,
                    ]}
                    noDataAvailableText={"No Payments Made Yet"}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid mt={5} pb={5}>
                {paymentDetails.length > 0 && (
                  <CustomTable
                    tableHeader={[
                      t("pages.payments.text11"),
                      t("pages.payments.text12"),
                      t("pages.payments.text13"),
                      t("pages.payments.text14"),
                    ]}
                    rows={getPaymentRowData}
                    isTotalRow={false}
                  />
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        {billDetails?.isInvoice && (
          <Grid
            display={"flex"}
            justifyContent={"center"}
            marginBottom={2}
            marginTop={1}
          >
            {/* <Button
              variant="contained"
              disabled={
                billDetails?.dueAmount <= 0 ||
                billDetails?.chargeStatusCode !== "BILLED"
              }
              mt={2}
              style={{
                alignSelf: "center",
                width: 106,
                height: 32,
                right: 10,
                marginLeft: 40,
                marginBottom: 3,
                display: "flex",
                borderRadius: 4,
                border: 1,
              }}
              onClick={() => {
                onPressPayNow();
              }}
            >
              <Typography variant="buttonText" color="common.buttonText">
                {t("pages.payments.text01")}
              </Typography>
            </Button> */}
          </Grid>
        )}
      </Grid>
      {loader && (
        <FullPageModal>
          <Grid>
            <Typography
              color={"white"}
              mt={2}
              fontSize={"17px"}
              fontWeight={"bold"}
            >
              Loading ...
            </Typography>
          </Grid>
        </FullPageModal>
      )}
    </div>
  );
};
